import { Alert, Button, Empty, Modal, Spin, Tag, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { usePosShiftStoreId } from '@/utils/posShift';
import type { PosVariantWarehouseStocksResponse, PosWarehouseStock } from '../types';
import { WarehouseSearchInput, filterWarehouses, useWarehouseKeyword } from './WarehouseSearchInput';

const { Text } = Typography;

interface WarehousePickerDialogProps {
  open: boolean;
  onClose: () => void;
  variantId: string;
  productName: string;
  /** Số lượng cần xuất của dòng hàng — kho tồn ít hơn sẽ bị disable. */
  requiredQty: number;
  currentWarehouseId?: string;
  /** Scope kho theo store này (màn sửa đơn — store của đơn). Bỏ trống → store của ca. */
  storeId?: string;
  /** null = "Bỏ chọn kho" (BE tự resolve như mặc định). */
  onSelect: (warehouse: PosWarehouseStock | null) => void;
}

/**
 * TCR-629: modal "Chọn kho xuất hàng" cho dòng hàng quantity (không serial)
 * trên POS — liệt kê kho của chi nhánh hiện tại kèm tồn on-hand.
 */
export function WarehousePickerDialog({
  open,
  onClose,
  variantId,
  productName,
  requiredQty,
  currentWarehouseId,
  storeId,
  onSelect,
}: WarehousePickerDialogProps) {
  // TCR-980: trên POS `storeId` KHÔNG được truyền (chỉ màn sửa đơn truyền) —
  // store thật đi kèm request do axios interceptor inject từ `pos.shiftStoreId`.
  // Store đó nằm ngoài query key thì đổi chi nhánh không làm key đổi và React
  // Query phục vụ lại danh sách kho của chi nhánh CŨ ngay khi modal đang mở.
  // Đưa store hiệu lực vào key; tham số gọi service vẫn là `storeId` để không
  // đổi hành vi màn sửa đơn.
  const shiftStoreId = usePosShiftStoreId();
  const effectiveStoreId = storeId ?? shiftStoreId ?? undefined;

  const { data, isFetching } = useQuery<PosVariantWarehouseStocksResponse>({
    queryKey: ['pos-warehouse-stocks', variantId, effectiveStoreId],
    queryFn: () => posService.listVariantWarehouseStocks(variantId, storeId),
    enabled: open && !!variantId,
    staleTime: 10_000,
    retry: false,
  });
  const warehouses = data?.items ?? [];
  const [keyword, setKeyword] = useWarehouseKeyword(open);
  const visibleWarehouses = filterWarehouses(warehouses, keyword);
  // TCR-1205 — BE phát hiện có tồn kho nhưng không khớp bất kỳ kho hiển thị
  // nào: kho đã bị đổi chi nhánh (tồn cũ còn giữ store_id chi nhánh trước —
  // ca thực tế hay gặp nhất) hoặc kho đã bị xoá. Không tự sửa được ở đây — chỉ cảnh
  // báo rõ để thu ngân biết đây là vấn đề DỮ LIỆU KHO, không phải hết hàng
  // thật, và báo quản lý điều chỉnh thay vì mò mẫm hoặc bỏ đơn.
  const unmatchedAvailable = data?.unmatched_available ?? 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Chọn kho xuất hàng"
      width={520}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Hủy
        </Button>,
        <Button
          key="clear"
          danger
          disabled={!currentWarehouseId}
          onClick={() => onSelect(null)}
        >
          Bỏ chọn kho
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 8 }}>
        <Text strong>{productName}</Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
          Cần {requiredQty} sản phẩm
        </Text>
      </div>

      {unmatchedAvailable > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Dữ liệu kho chưa khớp"
          description={`Hệ thống ghi nhận còn ${unmatchedAvailable} sản phẩm tồn ở kho không còn thuộc chi nhánh này — kho đã được chuyển sang chi nhánh khác hoặc đã bị xoá. Đây KHÔNG phải hết hàng, nhưng số tồn đó chưa bán được tại đây: vui lòng báo quản lý kho kiểm tra và điều chỉnh, tạm chọn kho khác bên dưới hoặc "Bỏ chọn kho" để chờ xử lý.`}
        />
      )}

      <WarehouseSearchInput open={open} value={keyword} onChange={setKeyword} />

      <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        {isFetching ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : warehouses.length === 0 ? (
          <Empty description="Chi nhánh chưa có kho nào" style={{ padding: 24 }} />
        ) : visibleWarehouses.length === 0 ? (
          <Empty description="Không tìm thấy kho phù hợp" style={{ padding: 24 }} />
        ) : (
          visibleWarehouses.map((w) => {
            const insufficient = w.available < requiredQty;
            const isSelected = w.warehouse_id === currentWarehouseId;
            return (
              <div
                key={w.warehouse_id}
                onClick={() => {
                  if (!insufficient) onSelect(w);
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 12px',
                  borderBottom: '1px solid #f5f5f5',
                  cursor: insufficient ? 'not-allowed' : 'pointer',
                  background: isSelected ? '#fff0f3' : '#fff',
                  opacity: insufficient ? 0.55 : 1,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div>
                    <Text strong>{w.name}</Text>
                    {w.is_main_warehouse && <Tag style={{ marginLeft: 6 }}>Kho chính</Tag>}
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {w.code}
                  </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <Tag color="#a4193d" style={{ margin: 0 }}>
                      Bán được: {w.available}
                    </Tag>
                    {w.reserved > 0 && (
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                        Tồn {w.quantity} · đang giữ {w.reserved}
                      </div>
                    )}
                  </div>
                  {isSelected && <CheckCircleFilled style={{ color: '#a4193d' }} />}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
